import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";

import ProcesosApi from "api/procesosApi";

// Hook personalizado para debounce
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

function SelectProcesoModal({ open, onClose, onSelect }) {
  const [procesos, setProcesos] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState(null);
  const searchInputRef = useRef(null);

  // Estados de paginación
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 0,
    total_items: 0,
    page_size: 10, // menos registros por página en modal
    has_next: false,
    has_previous: false,
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Obtener procesos con paginación
  const fetchProcesos = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);
        setError(null);

        const requestParams = {
          page: params.page || pagination?.current_page || 1,
          page_size: params.page_size || pagination?.page_size || 10,
          search: debouncedSearchTerm,
          ...params,
        };

        const response = await ProcesosApi.fetchProcesosPaginacion(requestParams);

        setProcesos(response.data.results);
        setPagination(response.data.pagination);
      } catch (error) {
        console.error("Error al cargar procesos:", error);
        setError(obtenerMensajeError(error, "Error al cargar los procesos"));
        setProcesos([]);
        setPagination((prev) => ({ ...(prev || {}), total_items: 0, total_pages: 0 }));
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    },
    [debouncedSearchTerm]
  );

  // Cargar datos cuando cambia la búsqueda
  useEffect(() => {
    if (open) {
      setPagination((prev) => ({ ...(prev || {}), current_page: 1 }));
      fetchProcesos({ page: 1 });
    }
  }, [debouncedSearchTerm, open, fetchProcesos]);

  // Carga inicial
  useEffect(() => {
    if (open) {
      setInitialLoading(true);
      fetchProcesos({ page: 1 });

      // Focus en el input de búsqueda
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }

    if (!open) {
      setSearchTerm("");
      setProcesos([]);
      setPagination({
        current_page: 1,
        total_pages: 0,
        total_items: 0,
        page_size: 10,
        has_next: false,
        has_previous: false,
      });
    }
  }, [open, fetchProcesos]);

  // Cambio de página
  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, current_page: newPage }));
    fetchProcesos({ page: newPage });
  };

  const handleClose = (event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      if (document.activeElement) {
        document.activeElement.blur();
      }
    }
    setSearchTerm("");
    onClose();
  };

  const handleProcesoSelect = (proceso) => {
    if (document.activeElement) {
      document.activeElement.blur();
    }
    onSelect(proceso);
    setSearchTerm("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={false}
      aria-labelledby="select-proceso-title"
    >
      <DialogTitle id="select-proceso-title" sx={{ mb: -3 }}>
        <Grid container>
          <Grid size={6}>
            <SuiTypography variant="h6" component="div">
              Buscar Proceso
            </SuiTypography>
          </Grid>
          <Grid size={6}>
            <SuiTypography variant="h6" component="div">
              {pagination?.total_items || 0} procesos encontrados
            </SuiTypography>
          </Grid>
        </Grid>
      </DialogTitle>

      <DialogContent>
        <SuiBox py={3}>
          <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              <SuiInput
                inputRef={searchInputRef}
                placeholder="Buscar por nombre del deudor o identificador"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                autoFocus
                disabled={loading}
              />
            </Grid>
          </Grid>

          {/* Loading inicial */}
          {initialLoading && (
            <Grid container>
              <Grid size={12}>
                <SuiBox
                  mt={3}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight={200}
                >
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <CircularProgress size={30} />
                    <SuiTypography variant="button" fontWeight="regular" sx={{ mt: 2 }}>
                      Cargando procesos...
                    </SuiTypography>
                  </Box>
                </SuiBox>
              </Grid>
            </Grid>
          )}

          {/* Error */}
          {error && (
            <Grid container>
              <Grid size={12}>
                <Alert severity="error" sx={{ mt: 1 }}>
                  {error}
                </Alert>
              </Grid>
            </Grid>
          )}

          {/* Paginación */}
          {(pagination?.total_pages || 0) > 1 && (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={1}
              sx={{ borderTop: "1px solid #e0e0e0" }}
            >
              <SuiTypography variant="caption" color="text">
                Página {pagination?.current_page || 1} de {pagination?.total_pages || 1}
              </SuiTypography>
              <Pagination
                count={pagination?.total_pages || 1}
                page={pagination?.current_page || 1}
                onChange={handlePageChange}
                disabled={loading}
                color="primary"
                size="small"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          {/* Lista */}
          {!initialLoading && (
            <SuiBox mt={3}>
              <Grid container>
                {procesos.map((proceso) => (
                  <Grid key={proceso.id} size={12}>
                    <SuiButton
                      fullWidth
                      variant="outlined"
                      buttonColor="dark"
                      onClick={() => handleProcesoSelect(proceso)}
                      disabled={loading}
                      sx={{
                        textAlign: "left",
                        justifyContent: "flex-start",
                        textTransform: "none",
                        mb: 1,
                      }}
                    >
                      <SuiBox>
                        <SuiTypography variant="button" fontWeight="medium">
                          {proceso.cliente_nombre}
                        </SuiTypography>
                        <SuiTypography variant="caption" color="text" display="block">
                          {proceso.identificador}
                        </SuiTypography>
                      </SuiBox>
                    </SuiButton>
                  </Grid>
                ))}

                {!initialLoading && !loading && procesos.length === 0 && (
                  <Grid size={12}>
                    <SuiBox textAlign="center" py={4}>
                      <SuiTypography variant="button" fontWeight="regular" color="text">
                        No se encontraron procesos
                      </SuiTypography>
                    </SuiBox>
                  </Grid>
                )}
              </Grid>

              {/* Loading para cambios de página */}
              {loading && !initialLoading && (
                <SuiBox display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={20} />
                </SuiBox>
              )}
            </SuiBox>
          )}
        </SuiBox>
      </DialogContent>

      <DialogActions>
        <SuiButton onClick={handleClose} buttonColor="light">
          Cancelar
        </SuiButton>
      </DialogActions>
    </Dialog>
  );
}

SelectProcesoModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default SelectProcesoModal;
